<script setup>
import Avatar from 'next/avatar/Avatar.vue';
import { ref, computed, watch, nextTick, onUnmounted } from 'vue';
import { useKeyboardNavigableList } from 'dashboard/composables/useKeyboardNavigableList';
import ContactAPI from 'dashboard/api/contacts';

const props = defineProps({
  searchKey: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['selectContact']);

const tagContactsRef = ref(null);
const selectedIndex = ref(0);
const contacts = ref([]);
const isLoading = ref(false);
let debounceTimer = null;

const items = computed(() => {
  return contacts.value.filter(
    contact => contact.email && contact.email.trim() !== ''
  );
});

const fetchContacts = async () => {
  if (isLoading.value) return;

  const trimmedSearch = props.searchKey.trim();
  if (trimmedSearch.length < 2) {
    contacts.value = [];
    return;
  }

  isLoading.value = true;
  try {
    const response = await ContactAPI.search(trimmedSearch, 1, 'name');
    contacts.value = response.data.payload || [];
  } catch (error) {
    contacts.value = [];
  } finally {
    isLoading.value = false;
  }
};

const debouncedFetchContacts = () => {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }
  debounceTimer = setTimeout(() => {
    fetchContacts();
  }, 200);
};

const adjustScroll = () => {
  nextTick(() => {
    if (tagContactsRef.value) {
      tagContactsRef.value.scrollTop = 50 * selectedIndex.value;
    }
  });
};

const onSelect = () => {
  emit('selectContact', items.value[selectedIndex.value]);
};

useKeyboardNavigableList({
  items,
  onSelect,
  adjustScroll,
  selectedIndex,
});

watch(items, newListOfContacts => {
  if (newListOfContacts.length < selectedIndex.value + 1) {
    selectedIndex.value = 0;
  }
});

watch(
  () => props.searchKey,
  () => {
    debouncedFetchContacts();
  }
);

onUnmounted(() => {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }
});

const onHover = index => {
  selectedIndex.value = index;
};

const onContactSelect = index => {
  selectedIndex.value = index;
  onSelect();
};
</script>

<template>
  <div>
    <ul
      v-if="items.length"
      ref="tagContactsRef"
      class="vertical dropdown menu mention--box bg-n-solid-1 p-1 rounded-xl text-sm overflow-auto absolute w-full z-20 shadow-md left-0 leading-[1.2] bottom-full max-h-[12.5rem] border border-solid border-n-strong"
    >
      <li
        v-for="(contact, index) in items"
        :id="`mention-item-${index}`"
        :key="contact.id"
        :class="{
          'bg-n-alpha-black2': index === selectedIndex,
          'last:mb-0': items.length <= 4,
        }"
        class="flex items-center px-2 py-1 rounded-md"
        @click="onContactSelect(index)"
        @mouseover="onHover(index)"
      >
        <div class="mr-2">
          <Avatar :src="contact.thumbnail" :name="contact.name" rounded-full />
        </div>
        <div
          class="flex-1 max-w-full overflow-hidden whitespace-nowrap text-ellipsis"
        >
          <h5
            class="mb-0 overflow-hidden text-sm text-n-slate-11 whitespace-nowrap text-ellipsis"
            :class="{
              'text-n-slate-12': index === selectedIndex,
            }"
          >
            {{ contact.name }}
          </h5>
          <div
            class="overflow-hidden text-xs whitespace-nowrap text-ellipsis text-n-slate-10"
            :class="{
              'text-n-slate-11': index === selectedIndex,
            }"
          >
            {{ contact.email }}
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>
